import { useMemo } from "react";
import type { ErrorLike } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { IssueListGroupCountsDocument } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

type UseIssueGroupCountsOptions = {
  where: Record<string, unknown> | undefined;
  groupBy: string | undefined;
};

type UseIssueGroupCountsResult = Omittable<{
  counts: Record<string, number>;
  ready: boolean;
  /** The query error, if any — a widget shows it as a `WidgetError` state. */
  error?: ErrorLike;
}>;

// The viewer's IANA zone, resolved once — time-series groupBys bucket in this
// calendar server-side (S7) so boundary activity lands in the viewer's period,
// not UTC's. Categorical groupBys ignore it. Module-level: the zone cannot
// change without a reload, and a stable value keeps query variables stable.
const VIEWER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function useIssueGroupCounts({ where, groupBy }: UseIssueGroupCountsOptions): UseIssueGroupCountsResult {
  const variables = useMemo(
    () =>
      where && groupBy
        ? { where, groupBy, timezone: VIEWER_TIMEZONE }
        : undefined,
    [where, groupBy],
  );

  const { data, error } = useQuery(IssueListGroupCountsDocument, {
    variables: variables!,
    skip: !variables,
    fetchPolicy: "cache-and-network",
  });

  const counts = useMemo(() => {
    const record: Record<string, number> = {};
    if (data?.issueGroupCounts) {
      for (const { key, total } of data.issueGroupCounts) {
        record[key] = total;
      }
    }
    return record;
  }, [data]);

  return { counts, ready: data?.issueGroupCounts !== undefined, error };
}
